import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2, Lock, AlertTriangle, CheckCircle2, LogIn, Home } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';

export default function DeleteAccount() {
  const { customer, customerToken, deleteAccount } = useCustomerAuth();

  const [password, setPassword] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  const handleDelete = async (e) => {
    e.preventDefault();
    if (!password) {
      setError('Please enter your password to confirm deletion.');
      return;
    }
    if (!confirmed) {
      setError('You must confirm that you understand account deletion is permanent.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await deleteAccount(password);
      setIsDeleted(true);
    } catch (err) {
      setError(err.message || 'Failed to delete account. Please verify your password.');
    } finally {
      setLoading(false);
    }
  };

  // Case 1: Account successfully deleted
  if (isDeleted) {
    return (
      <main className="min-h-screen pt-24 pb-16 bg-[#f8fafc] flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="shadow-lg border-slate-200 bg-white text-center">
              <CardContent className="pt-8 pb-8 px-6 space-y-4">
                <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                  <CheckCircle2 size={36} />
                </div>
                <CardTitle className="text-2xl font-bold text-slate-900 font-['Oswald']">
                  Account Deleted
                </CardTitle>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Your VHFMS account has been successfully deleted.
                </p>
                <p className="text-slate-500 text-xs">
                  Your personal information and profile details have been anonymized in accordance with our Privacy Policy.
                </p>
                <div className="pt-4">
                  <Link to="/">
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white font-medium gap-2">
                      <Home size={16} />
                      <span>Back to Home</span>
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    );
  }

  // Case 2: Customer is NOT logged in
  if (!customerToken) {
    return (
      <main className="min-h-screen pt-24 pb-16 bg-[#f8fafc] flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="shadow-lg border-slate-200 bg-white">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-3 text-red-600">
                  <Trash2 size={24} />
                </div>
                <CardTitle className="text-2xl font-bold text-slate-900 font-['Oswald']">
                  Delete Customer Account
                </CardTitle>
                <CardDescription className="text-slate-500 text-sm">
                  VHFMS Account Deletion Request
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-5 text-center">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-left text-amber-900 text-sm space-y-2">
                  <div className="flex items-center gap-2 font-semibold text-amber-800">
                    <AlertTriangle size={18} className="flex-shrink-0" />
                    <span>Authentication Required</span>
                  </div>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    To protect your account from unauthorized deletion, you must sign in to your VHFMS account first to request account deletion.
                  </p>
                </div>

                <p className="text-xs text-slate-500 leading-relaxed text-left">
                  Once authenticated, you will be able to confirm deletion by entering your account password.
                </p>

                <div className="pt-2">
                  <Link to="/customer-login">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium gap-2 py-2.5">
                      <LogIn size={16} />
                      <span>Login to Delete Account</span>
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    );
  }

  // Case 3: Customer IS logged in
  return (
    <main className="min-h-screen pt-24 pb-16 bg-[#f8fafc] flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="shadow-lg border-red-200 bg-white">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-3 text-red-600">
                <Trash2 size={24} />
              </div>
              <CardTitle className="text-2xl font-bold text-red-600 font-['Oswald']">
                Confirm Account Deletion
              </CardTitle>
              <CardDescription className="text-slate-500 text-sm">
                This action is permanent and cannot be undone
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Customer info */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm">
                <p className="text-xs text-slate-400 font-medium">Logged in as:</p>
                <p className="font-semibold text-slate-900">{customer?.name || 'Customer'}</p>
                <p className="text-xs text-slate-600">{customer?.mobile ? `+91 ${customer.mobile}` : ''}</p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleDelete} className="space-y-4">
                {/* Password field */}
                <div className="space-y-1.5">
                  <Label htmlFor="delete-password">Enter Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="delete-password"
                      type="password"
                      placeholder="Your account password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (error) setError('');
                      }}
                      className="pl-9"
                      required
                    />
                  </div>
                </div>

                {/* Confirmation Checkbox */}
                <div className="flex items-start gap-2 pt-1">
                  <Checkbox
                    id="confirm-delete"
                    checked={confirmed}
                    onCheckedChange={(checked) => {
                      setConfirmed(!!checked);
                      if (error) setError('');
                    }}
                    className="mt-1 border-red-400 data-[state=checked]:bg-red-600"
                  />
                  <label htmlFor="confirm-delete" className="text-xs text-slate-700 leading-normal font-medium">
                    I understand that deleting my account is permanent.
                  </label>
                </div>

                {/* Submit button */}
                <Button
                  type="submit"
                  disabled={loading}
                  variant="destructive"
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2.5 rounded-lg transition-colors mt-2"
                >
                  {loading ? 'Deleting Account...' : 'Permanently Delete Account'}
                </Button>
              </form>

              <div className="text-center pt-2">
                <Link to="/customer-account" className="text-xs text-slate-500 hover:text-slate-800 underline">
                  Cancel and return to profile
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </main>
  );
}
